import { z } from 'zod';
import { isoDate } from './common.js';

/**
 * Разрешение на выборки дней и журнал выборок (005, T132, T136; specs/005-rules-mcp/contracts/http-api.md; FR-023–FR-028,
 * FR-064). Одна схема для адресов модуля `agent` и страниц интерфейса:
 * - `GET` / `PUT /api/admin/agent-data` — разрешение объекта (администратор системы);
 * - `GET /api/admin/agent-data/samples` — журнал выборок: кто, клиент, условие, человеко-дни (администратор системы);
 * - `GET /api/me/samples` — свои выборки без списка людей;
 * - `POST /api/days/:personId/:date/agent-link` — «Передать день агенту».
 * ФИО людей в схемах нет: люди — номером (`personId`) и только в журнале администратора системы.
 */

/** Вид основания (FR-028): договор с обработчиком или согласие работников. */
export const AGENT_DATA_BASIS_KINDS = ['договор_с_обработчиком', 'согласие_работников'] as const;
export const AgentDataBasisKind = z.enum(AGENT_DATA_BASIS_KINDS);

/** Лимиты выборок (FR-023): дней в одном ответе (до 20) и в сутки на пользователя. */
export const SAMPLE_PER_RESPONSE_MAX = 20;
export const SAMPLE_PER_DAY_MAX = 10_000;
export const SampleLimits = z.object({
  perResponse: z.number().int().min(1).max(SAMPLE_PER_RESPONSE_MAX),
  perDay: z.number().int().min(1).max(SAMPLE_PER_DAY_MAX),
});
export type SampleLimits = z.infer<typeof SampleLimits>;

/** Клиент агента: адрес документа клиента (CIMD), наше название по хосту, хост. */
export const AgentDataClient = z.object({ id: z.string(), name: z.string(), host: z.string() });
export type AgentDataClient = z.infer<typeof AgentDataClient>;

/** Основание разрешения: вид, номер, дата. */
export const AgentDataBasis = z.object({ kind: AgentDataBasisKind, ref: z.string(), date: isoDate });
export type AgentDataBasis = z.infer<typeof AgentDataBasis>;

/** Редакция разрешения в истории: что было записано, кто и когда записал. */
export const AgentDataRevision = z.object({
  id: z.number().int().positive(),
  enabled: z.boolean(),
  basis: AgentDataBasis.nullable(),
  clients: z.array(AgentDataClient),
  limits: SampleLimits,
  setAt: z.string(),
  setBy: z.object({ id: z.number().int(), name: z.string() }),
});
export type AgentDataRevision = z.infer<typeof AgentDataRevision>;

/**
 * Ответ `GET` и `PUT /api/admin/agent-data`: разрешение сейчас, клиенты, из которых можно выбрать (белый список входа
 * OAuth), история — новые сверху. Записей нет — выключено, лимиты по умолчанию.
 */
export const AgentDataView = z.object({
  enabled: z.boolean(),
  basis: AgentDataBasis.nullable(),
  clients: z.array(AgentDataClient),
  limits: SampleLimits,
  /** Срок разрешения из 002 (включительно); в 005 не задаётся. */
  validTo: isoDate.nullable(),
  available: z.array(AgentDataClient),
  history: z.array(AgentDataRevision),
});
export type AgentDataView = z.infer<typeof AgentDataView>;

/**
 * Тело `PUT /api/admin/agent-data`: включить, выключить или изменить. Включение без основания (вид, номер, дата) —
 * `400 BASIS_REQUIRED`; без клиентов или с клиентом не из белого списка — `400 VALIDATION`. Выключение отзывает
 * ссылки выборок.
 */
export const AgentDataPut = z.strictObject({
  enabled: z.boolean(),
  basisKind: AgentDataBasisKind.nullable().optional(),
  basisRef: z.string().trim().max(200).nullable().optional(),
  basisDate: isoDate.nullable().optional(),
  clients: z.array(z.string().min(1).max(500)).max(10),
  limits: SampleLimits,
});
export type AgentDataPut = z.infer<typeof AgentDataPut>;

/** Откуда выборка: `condition` — агент по условию, `day_link` — «Передать день агенту». */
export const DAY_SAMPLE_SOURCES = ['condition', 'day_link'] as const;
export const DaySampleSource = z.enum(DAY_SAMPLE_SOURCES);
export type DaySampleSource = z.infer<typeof DaySampleSource>;

/** Условие выборки, как его прислал агент (FR-023); у `day_link` — `{}`. */
export const SampleConditionView = z.record(z.string(), z.unknown());

/** Запрос журнала выборок: период по дате выборки (включительно, по поясу объекта) и человек. */
export const SamplesLogQuery = z.object({
  from: isoDate.optional(),
  to: isoDate.optional(),
  personId: z.string().min(1).max(64).optional(),
});
export type SamplesLogQuery = z.infer<typeof SamplesLogQuery>;

/** Выборка в журнале администратора системы: кто, клиент, условие, сколько дней и какие человеко-дни ушли. */
export const SampleLogEntry = z.object({
  id: z.number().int().positive(),
  createdAt: z.string(),
  user: z.object({ id: z.number().int(), name: z.string() }),
  /** Клиент агента; выборка «Передать день агенту» из интерфейса — `null`. */
  client: AgentDataClient.nullable(),
  source: DaySampleSource,
  condition: SampleConditionView,
  days: z.number().int().nonnegative(),
  smallGroup: z.boolean(),
  /** Человеко-дни выборки (номер человека и дата); с `personId` в запросе — только дни этого человека. */
  items: z.array(z.object({ personId: z.string(), date: isoDate })),
});
export type SampleLogEntry = z.infer<typeof SampleLogEntry>;

/** Ответ `GET /api/admin/agent-data/samples`: новые сверху, не больше 500; больше — `truncated`. */
export const SamplesLogResponse = z.object({ samples: z.array(SampleLogEntry), truncated: z.boolean() });
export type SamplesLogResponse = z.infer<typeof SamplesLogResponse>;

/** Своя выборка (FR-026): номер, условие, число дней, время, клиент — без списка людей. */
export const MySample = z.object({
  id: z.number().int().positive(),
  createdAt: z.string(),
  client: AgentDataClient.nullable(),
  source: DaySampleSource,
  condition: SampleConditionView,
  days: z.number().int().nonnegative(),
});
export type MySample = z.infer<typeof MySample>;

/**
 * Ответ `GET /api/me/samples`: свои выборки, новые сверху, не больше 100. `canShareDay` — кнопка «Передать день агенту»
 * на карточке дня видна (FR-064): выборки на объекте включены и у пользователя есть право «ИИ-агент»; роль из строки
 * «Выборки дней» уже проверена правом адреса (другим ролям — 403).
 */
export const MySamplesResponse = z.object({ canShareDay: z.boolean(), samples: z.array(MySample) });
export type MySamplesResponse = z.infer<typeof MySamplesResponse>;

/** Ответ `POST /api/days/:personId/:date/agent-link` (FR-064): ссылка для разговора с агентом, срок, номер выборки. */
export const AgentLinkResponse = z.object({
  link: z.string(),
  expiresAt: z.string(),
  sampleId: z.number().int().positive(),
});
export type AgentLinkResponse = z.infer<typeof AgentLinkResponse>;
